<script setup lang="ts">
  import { ref, onMounted, computed, nextTick, watch } from 'vue'
  import { Eye, Pencil, Check, X, AlertTriangle, Scissors } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import type { Document, CustomerCase } from '@/types/generated/types.gen'
  import { genericErrorToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { useRouter, useRoute } from 'vue-router'
  import { useUserStore } from '@/store/userStore'

  const userStore = useUserStore()

  const { t } = useTranslation({
    en: {
      documentsTitle: 'Documents ({count})',
      selectDocumentsSubline: 'Select which documents you want to use for the case file.',
      totalSize: 'Total size:',
      maxPerFile: '(max. 35 MB per file)',
      limitExceeded: 'Limit exceeded! Please remove some documents',
      orIncreaseLimit: 'or increase the limit',
      here: 'here',
      largeDocumentsTitle: 'Large documents detected',
      largeDocumentsMessage:
        'One or more documents exceed the maximum size of 35 MB and cannot be exported. RA-MICRO does not currently process files over 35 MB reliably. Please split the affected documents into smaller parts to export them.',
      documentName: 'Document name',
      source: 'Source',
      created: 'Created',
      size: 'Size',
      splitDocuments: 'Split documents',
      documentTooLargeTitle: 'Document is larger than 35 MB'
    },
    de: {
      documentsTitle: 'Dokumente ({count})',
      selectDocumentsSubline:
        'Wählen Sie aus, welche Dokumente Sie zur Aktenanlage verwenden möchten.',
      totalSize: 'Gesamtgröße:',
      maxPerFile: '(max. 35 MB pro Datei)',
      limitExceeded: 'Limit überschritten! Bitte entfernen Sie einige Dokumente',
      orIncreaseLimit: 'oder erhöhen Sie das Limit',
      here: 'hier',
      largeDocumentsTitle: 'Große Dokumente erkannt',
      largeDocumentsMessage:
        'Ein oder mehrere Dokumente überschreiten die zulässige Größe von 35 MB und können daher nicht exportiert werden. RA-MICRO verarbeitet Dateien über 35 MB derzeit nicht zuverlässig. Bitte teilen Sie die betroffenen Dokumente in kleinere Teile auf, um sie exportieren zu können.',
      documentName: 'Dokument Name',
      source: 'Ursprung',
      created: 'Erstellungsdatum',
      size: 'Größe',
      splitDocuments: 'Dokumente aufteilen',
      documentTooLargeTitle: 'Dokument ist größer als 35 MB'
    }
  })

  const router = useRouter()
  const route = useRoute()

  const returnPath = computed(() => {
    const name = route.params.integration_name
    const uuid = route.params.uuid
    if (name && uuid) {
      return `/app/integrations/${name}/export/${uuid}`
    }
    return ''
  })

  const props = defineProps<{
    caseData?: CustomerCase
    documents?: Document[]
    integrationMode?: string
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['updateDocuments', 'update:fileData', 'sizeValidation'])

  interface ExportDocument extends Document {
    isSelected: boolean
    isEditing: boolean
    newName: string
    size?: number
    sizeFormatted?: string
    exceedsJurnodesLimit?: boolean
    sizeEstimated?: boolean
  }

  const exportDocuments = ref<ExportDocument[]>([])
  const updateTrigger = ref(0)

  const isRaMicroApiMode = computed(() => props.integrationMode === 'RaMicroApi')
  const maxRaMicroApiFileSize = computed(() => {
    const limitMb = userStore.user?.org?.ra_micro_upload_limit_mb || 25
    return limitMb * 1024 * 1024
  })

  const maxRaMicroApiFileSizeMb = computed(() => {
    return userStore.user?.org?.ra_micro_upload_limit_mb || 25
  })

  const largeFileThreshold = 35 * 1024 * 1024 // 35MB in bytes

  const hasLargeDocuments = computed(() => {
    if (!isRaMicroApiMode.value) return false
    return exportDocuments.value.some((doc) => (doc.size || 0) > largeFileThreshold)
  })

  const navigateToOrganizePages = (doc?: ExportDocument) => {
    const caseUuid = props.caseData?.uuid
    if (!caseUuid) return
    const documentIds = doc?.uuid ? [doc.uuid] : []
    router.push({
      name: 'Organize Pages',
      params: { caseId: caseUuid },
      state: {
        documentIds,
        ...(returnPath.value && { returnPath: returnPath.value })
      }
    })
  }

  const isDocumentTooLargeForSelection = (doc: ExportDocument): boolean => {
    return isRaMicroApiMode.value && (doc.size || 0) > largeFileThreshold
  }

  const selectableDocuments = computed(() =>
    exportDocuments.value.filter((doc) => !isDocumentTooLargeForSelection(doc))
  )

  /**
   * Fetches the file size from a URL using a HEAD request
   * @param url The URL to fetch the file size from
   * @returns Promise that resolves to the file size in bytes, or null if it couldn't be determined
   */
  const fetchFileSizeFromUrl = async (url: string): Promise<number | null> => {
    try {
      const response = await fetch(url, { method: 'HEAD' })
      if (!response.ok) {
        console.error(`Failed to fetch file size: ${response.status} ${response.statusText}`)
        return null
      }

      const contentLength = response.headers.get('content-length')
      if (contentLength) {
        return parseInt(contentLength, 10)
      }

      return null
    } catch (error) {
      console.error('Error fetching file size:', error)
      return null
    }
  }

  onMounted(async () => {
    try {
      // Use documents from props, or fall back to caseData.documents for backward compatibility
      const sourceDocuments = props.documents || props.caseData?.documents || []

      // Create initial document objects
      exportDocuments.value = await Promise.all(
        sourceDocuments.map(async (doc) => {
          // Try to determine file size or complexity using multiple properties
          let size = doc.filesize || 0
          let sizeEstimated = false
          if (size === 0 && doc.url) {
            // Try to get file size from URL
            const urlSize = await fetchFileSizeFromUrl(doc.url)
            if (urlSize) {
              size = urlSize
              sizeEstimated = true
            }
            // Fallback to estimation methods if URL fetch fails
            else if (doc.page_count && doc.page_count > 0) {
              // Rough estimate: ~100KB per page
              size = doc.page_count * 100 * 1024
              sizeEstimated = true
            } else if (doc.images && doc.images.length > 0) {
              // Rough estimate: ~100KB per image
              size = doc.images.length * 100 * 1024
              sizeEstimated = true
            }
          }

          // No need to check for Jurnodes limit anymore
          const exceedsLimit = false
          const tooLargeForRaMicroApi =
            props.integrationMode === 'RaMicroApi' && size > largeFileThreshold
          const initiallySelected = !exceedsLimit && !tooLargeForRaMicroApi

          try {
            return {
              ...doc,
              isSelected: initiallySelected,
              isEditing: false,
              newName: doc.filename.replace('.pdf', ''),
              size: size,
              sizeFormatted: formatFileSize(size),
              sizeEstimated: sizeEstimated,
              exceedsJurnodesLimit: false
            }
          } catch (error) {
            genericErrorToast()
          }
        })
      )

      emitFileData()
    } catch (error) {
      genericErrorToast()
      console.error(error)
    }
  })

  const selectedCount = computed(() => exportDocuments.value.filter((doc) => doc.isSelected).length)

  // Calculate total size of selected documents
  const totalSelectedSize = computed(() => {
    return exportDocuments.value
      .filter((doc) => doc.isSelected)
      .reduce((total, doc) => total + (doc.size || 0), 0)
  })

  // Format the total size
  const totalSelectedSizeFormatted = computed(() => {
    return formatFileSize(totalSelectedSize.value)
  })

  // Check if total size exceeds RaMicroAPI limit
  const exceedsRaMicroApiLimit = computed(() => {
    return isRaMicroApiMode.value && totalSelectedSize.value > maxRaMicroApiFileSize.value
  })

  // Watch for changes in total size and emit validation status
  watch(exceedsRaMicroApiLimit, (exceeds) => {
    emit('sizeValidation', !exceeds)
  })

  const toggleSelection = (doc: ExportDocument, isSelected: boolean) => {
    // Prevent selection of documents over 35MB in RaMicroApi (must split first)
    if (isDocumentTooLargeForSelection(doc) && isSelected) {
      return
    }
    // Prevent selection if adding this document would exceed RaMicroAPI limit
    if (isRaMicroApiMode.value && isSelected) {
      const newTotalSize = totalSelectedSize.value + (doc.size || 0)
      if (newTotalSize > maxRaMicroApiFileSize.value) {
        return
      }
    }

    doc.isSelected = isSelected
    emitFileData()
  }

  const deselectAll = () => {
    exportDocuments.value.forEach((doc) => {
      doc.isSelected = false
    })
    updateTrigger.value += 1
    nextTick(() => {
      emitFileData()
    })
  }

  const selectAll = () => {
    exportDocuments.value.forEach((doc) => {
      doc.isSelected = !isDocumentTooLargeForSelection(doc)
    })
    updateTrigger.value += 1
    nextTick(() => {
      emitFileData()
    })
  }

  const allSelected = computed({
    get: () => {
      const selectable = selectableDocuments.value
      if (selectable.length === 0) return false
      const selectedSelectable = selectable.filter((doc) => doc.isSelected).length
      return selectedSelectable === selectable.length
    },
    set: (value: boolean) => {
      if (value) {
        selectAll()
      } else {
        deselectAll()
      }
    }
  })

  const viewDocument = (doc: ExportDocument) => {
    window.open(doc.url, '_blank')
  }

  const editDocument = (doc: ExportDocument) => {
    doc.isEditing = true
    doc.newName = doc.filename.replace('.pdf', '')
  }

  const saveDocumentName = (doc: ExportDocument) => {
    doc.filename = `${doc.newName}.pdf`
    doc.isEditing = false
    emitFileData()
  }

  const cancelEdit = (doc: ExportDocument) => {
    doc.isEditing = false
    doc.newName = doc.filename.replace('.pdf', '')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'

    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const fileData = computed(() => {
    return exportDocuments.value
      .filter((doc) => doc.isSelected)
      .map((doc) => ({
        uuid: doc.uuid,
        filename: doc.filename
      }))
  })

  const emitFileData = () => {
    emit('update:fileData', fileData.value)
    emit(
      'updateDocuments',
      exportDocuments.value.filter((doc) => doc.isSelected)
    )
  }
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold text-gray-900">
      {{ t('documentsTitle', { count: selectedCount }) }}
    </h2>
    <p class="mb-4 text-gray-600">
      {{ t('selectDocumentsSubline') }}
      <span
        v-if="isRaMicroApiMode"
        class="mt-2 block font-medium"
        :class="{
          'text-amber-600': exceedsRaMicroApiLimit,
          'text-gray-600': !exceedsRaMicroApiLimit
        }"
      >
        {{ t('totalSize') }} {{ totalSelectedSizeFormatted }} / {{ maxRaMicroApiFileSizeMb }} MB
        {{ t('maxPerFile') }}
        <span
          v-if="exceedsRaMicroApiLimit"
          class="font-bold text-red-600"
        >
          ({{ t('limitExceeded') }}
          <template v-if="maxRaMicroApiFileSizeMb < 100">
            {{ t('orIncreaseLimit') }}
            <a
              href="/app/settings/law-office-software/ra-micro"
              class="underline hover:text-red-800"
            >
              {{ t('here') }}
            </a>
          </template>
          .)
        </span>
      </span>
    </p>

    <div
      v-if="hasLargeDocuments"
      class="mb-4 rounded-lg border-l-4 border-orange-500 bg-orange-50 p-4"
    >
      <div class="flex items-start">
        <AlertTriangle class="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" />
        <div class="flex-1">
          <h3 class="font-semibold text-orange-900">{{ t('largeDocumentsTitle') }}</h3>
          <p class="mt-1 text-sm text-orange-800">
            {{ t('largeDocumentsMessage') }}
          </p>
        </div>
      </div>
    </div>

    <Table>
      <TableHeader>
        <TableRow class="bg-slate-50">
          <TableHead>
            <CheckboxField
              v-model="allSelected"
              name="select-all-documents"
              :checkedValue="true"
              :uncheckedValue="false"
            />
          </TableHead>
          <TableHead class="w-[560px] max-w-[560px]">{{ t('documentName') }}</TableHead>
          <TableHead>{{ t('source') }}</TableHead>
          <TableHead>{{ t('created') }}</TableHead>
          <TableHead>{{ t('size') }}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow
          v-for="doc in exportDocuments"
          :key="`${doc.uuid}-${updateTrigger}`"
          class="hover:bg-slate-50"
        >
          <TableCell
            class="w-12"
            :class="{ 'opacity-60': isDocumentTooLargeForSelection(doc) }"
          >
            <CheckboxField
              v-model="doc.isSelected"
              :name="`document-${doc.uuid}`"
              :checkedValue="true"
              :uncheckedValue="false"
              :disabled="isDocumentTooLargeForSelection(doc)"
              @update:model-value="(isSelected: boolean) => toggleSelection(doc, isSelected)"
            />
          </TableCell>
          <TableCell class="w-[560px] min-w-0 max-w-[560px] p-2">
            <div class="flex min-w-0 items-center justify-between gap-2">
              <div
                class="flex min-w-0 flex-grow items-center gap-0.5"
                :class="{ 'opacity-60': isDocumentTooLargeForSelection(doc) }"
              >
                <template v-if="doc.isEditing">
                  <Input
                    v-model="doc.newName"
                    class="min-w-0 flex-1"
                  />
                  <span class="shrink-0 text-gray-500">.pdf</span>
                </template>
                <template v-else>
                  <span
                    class="min-w-0 truncate"
                    :title="doc.filename"
                  >
                    {{ doc.filename.replace('.pdf', '') }}
                  </span>
                  <span class="shrink-0 text-gray-500">.pdf</span>
                </template>
              </div>
              <div class="flex shrink-0 flex-wrap items-center gap-2">
                <Button
                  v-if="isDocumentTooLargeForSelection(doc)"
                  size="sm"
                  variant="default"
                  class="shrink-0 bg-orange-600 font-semibold text-white shadow-sm hover:bg-orange-700
                    focus-visible:ring-orange-500"
                  @click="navigateToOrganizePages(doc)"
                >
                  <Scissors class="mr-2 h-4 w-4 shrink-0" />
                  {{ t('splitDocuments') }}
                </Button>
                <template v-if="doc.isEditing">
                  <Button
                    variant="secondary"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="saveDocumentName(doc)"
                  >
                    <Check class="h-4 w-4 text-green-500" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="cancelEdit(doc)"
                  >
                    <X class="h-4 w-4 text-red-500" />
                  </Button>
                </template>
                <template v-else>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="viewDocument(doc)"
                  >
                    <Eye class="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="editDocument(doc)"
                  >
                    <Pencil class="h-4 w-4 text-slate-500" />
                  </Button>
                </template>
              </div>
            </div>
          </TableCell>
          <TableCell
            class="p-2"
            :class="{ 'opacity-60': isDocumentTooLargeForSelection(doc) }"
          >
            {{ doc.document_type || 'Unknown' }}
          </TableCell>
          <TableCell
            class="p-2"
            :class="{ 'opacity-60': isDocumentTooLargeForSelection(doc) }"
          >
            {{ formatDate(doc.created_at) }}
          </TableCell>
          <TableCell
            class="p-2"
            :class="{ 'opacity-60': isDocumentTooLargeForSelection(doc) }"
          >
            <div class="flex items-center gap-2">
              <span>{{ doc.sizeFormatted }}</span>
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
